<template>
  <form class="space-y-6" @submit.prevent="handleSubmit">
    <!-- Titre -->
    <div class="text-center">
      <h2 class="text-3xl font-bold text-gray-900">
        Créer un compte
      </h2>
      <p class="mt-2 text-sm text-gray-600">
        Rejoignez la plateforme Anamnesis
      </p>
    </div>

    <!-- Alert d'erreur -->
    <Alert v-if="error" type="error" :message="error" :show="!!error" @close="clearError" />

    <!-- Alert de succès -->
    <Alert v-if="successMessage" type="success" :message="successMessage" :show="!!successMessage"
      @close="successMessage = ''" />

    <!-- Champ Nom -->
    <InputField v-model="formData.name" type="text" name="name" label="Nom complet" placeholder="Votre nom complet"
      :required="true" :error-message="errors.name" :disabled="isSubmitting" autocomplete="name" :left-icon="UserIcon"
      @blur="validateField('name')" />

    <!-- Champ Email -->
    <InputField v-model="formData.email" type="email" name="email" label="Adresse email"
      placeholder="votre.email@example.com" :required="true" :error-message="errors.email" :disabled="isSubmitting"
      autocomplete="email" :left-icon="MailIcon" @blur="validateField('email')" />

    <!-- Champ Mot de passe -->
    <PasswordField v-model="formData.password" name="password" label="Mot de passe"
      placeholder="Choisissez un mot de passe fort" :required="true" :error-message="errors.password"
      :disabled="isSubmitting" autocomplete="new-password" :left-icon="LockIcon" :toggleable="true"
      :show-strength-indicator="true" @blur="validateField('password')"
      @strength-change="handlePasswordStrengthChange" />

    <!-- Champ Confirmation mot de passe -->
    <PasswordField v-model="formData.confirmPassword" name="confirmPassword" label="Confirmer le mot de passe"
      placeholder="Confirmer votre mot de passe" :required="true" :error-message="errors.confirmPassword"
      :disabled="isSubmitting" autocomplete="new-password" :left-icon="LockIcon" :toggleable="true"
      @blur="validateField('confirmPassword')" />

    <!-- Champs optionnels -->
    <div class="space-y-4 border-t pt-6">
      <h3 class="text-sm font-medium text-gray-700">
        Informations complémentaires (optionnel)
      </h3>

      <!-- Fonction -->
      <InputField v-model="formData.fonction" type="text" name="fonction" label="Fonction"
        placeholder="Ex: Archéologue, Restaurateur, Chercheur..." :error-message="errors.fonction"
        :disabled="isSubmitting" autocomplete="organization-title" :left-icon="BriefcaseIcon"
        @blur="validateField('fonction')" />

      <!-- Affiliation -->
      <InputField v-model="formData.affiliation" type="text" name="affiliation" label="Affiliation"
        placeholder="Ex: Université, Musée, Institut..." :error-message="errors.affiliation" :disabled="isSubmitting"
        autocomplete="organization" :left-icon="BuildingIcon" @blur="validateField('affiliation')" />
    </div>

    <!-- Conditions d'utilisation -->
    <div class="flex items-start">
      <div class="flex items-center h-5">
        <input id="accept-terms" v-model="formData.acceptTerms" type="checkbox"
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          :class="{ 'border-red-300': errors.acceptTerms }" />
      </div>
      <div class="ml-3 text-sm">
        <label for="accept-terms" class="text-gray-700">
          J'accepte les
          <button type="button" class="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline"
            @click="$emit('show-terms')">
            conditions d'utilisation
          </button>
          et la
          <button type="button" class="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline"
            @click="$emit('show-privacy')">
            politique de confidentialité
          </button>
        </label>
        <p v-if="errors.acceptTerms" class="mt-1 text-xs text-red-600">
          {{ errors.acceptTerms }}
        </p>
      </div>
    </div>

    <!-- Bouton de soumission -->
    <button type="submit" :disabled="isSubmitting || !isFormValid" :class="submitButtonClasses">
      <LoadingSpinner v-if="isSubmitting" size="sm" variant="white" :show-text="false" />
      <span v-else>Créer mon compte</span>
    </button>

    <!-- Lien connexion -->
    <div class="text-center">
      <p class="text-sm text-gray-600">
        Déjà un compte ?
        <button type="button"
          class="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition-colors"
          @click="$emit('switch-to-login')">
          Se connecter
        </button>
      </p>
    </div>

    <!-- Notification pour les erreurs -->
    <Notification ref="notification" />
  </form>
</template>

<script setup lang="ts">
import InputField from '@/components/form/InputField.vue';
import PasswordField from '@/components/form/PasswordField.vue';
import Notification from '@/components/Notification.vue';
import Alert from '@/components/UI/Alert.vue';
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue';
import { authService } from '@/services/authService';
import type { AuthError, PasswordStrength, RegisterRequest } from '@/types/auth';
import {
  Briefcase as BriefcaseIcon,
  Building as BuildingIcon,
  Lock as LockIcon,
  Mail as MailIcon,
  User as UserIcon
} from 'lucide-vue-next';
import { computed, reactive, ref } from 'vue';

export interface RegisterFormEmits {
  (e: 'submit', userData: RegisterRequest): void;
  (e: 'switch-to-login'): void;
  (e: 'show-terms'): void;
  (e: 'show-privacy'): void;
}

const emit = defineEmits<RegisterFormEmits>();

// Références
const notification = ref<InstanceType<typeof Notification> | null>(null);

// État du formulaire
const formData = reactive({
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  fonction: '',
  affiliation: '',
  acceptTerms: false,
});

const errors = reactive({
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  fonction: '',
  affiliation: '',
  acceptTerms: '',
});

const error = ref<string>('');
const successMessage = ref<string>('');
const isSubmitting = ref(false);
const passwordStrength = ref<PasswordStrength>({
  score: 0,
  feedback: [],
  isValid: false,
});

// Validation
const isFormValid = computed((): boolean => {
  return !!(
    formData.name &&
    formData.email &&
    formData.password &&
    formData.confirmPassword &&
    formData.acceptTerms &&
    !errors.name &&
    !errors.email &&
    !errors.password &&
    !errors.confirmPassword &&
    !errors.acceptTerms &&
    passwordStrength.value.isValid
  );
});

const validateField = (field: keyof typeof errors): void => {
  switch (field) {
    case 'name':
      if (!formData.name) {
        errors.name = 'Le nom est requis';
      } else if (formData.name.trim().length < 2) {
        errors.name = 'Le nom doit contenir au moins 2 caractères';
      } else if (formData.name.trim().length > 100) {
        errors.name = 'Le nom ne peut pas dépasser 100 caractères';
      } else {
        errors.name = '';
      }
      break;

    case 'email':
      if (!formData.email) {
        errors.email = 'L\'email est requis';
      } else if (!authService.isValidEmail(formData.email)) {
        errors.email = 'Format d\'email invalide';
      } else {
        errors.email = '';
      }
      break;

    case 'password':
      if (!formData.password) {
        errors.password = 'Le mot de passe est requis';
        passwordStrength.value = { score: 0, feedback: [], isValid: false };
      } else {
        const validation = authService.validatePassword(formData.password);
        passwordStrength.value = {
          score: validation.errors.length === 0 ? 4 : Math.max(0, 4 - validation.errors.length),
          feedback: validation.errors,
          isValid: validation.isValid,
        };

        if (!validation.isValid) {
          errors.password = validation.errors[0] || 'Mot de passe trop faible';
        } else {
          errors.password = '';
        }

        // Revalider la confirmation si elle existe
        if (formData.confirmPassword) {
          validateField('confirmPassword');
        }
      }
      break;

    case 'confirmPassword':
      if (!formData.confirmPassword) {
        errors.confirmPassword = 'La confirmation du mot de passe est requise';
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas';
      } else {
        errors.confirmPassword = '';
      }
      break;

    case 'fonction':
      // Pas de validation spécifique, champ optionnel
      errors.fonction = '';
      break;

    case 'affiliation':
      // Pas de validation spécifique, champ optionnel
      errors.affiliation = '';
      break;

    case 'acceptTerms':
      if (!formData.acceptTerms) {
        errors.acceptTerms = 'Vous devez accepter les conditions d\'utilisation';
      } else {
        errors.acceptTerms = '';
      }
      break;
  }
};

const validateForm = (): boolean => {
  validateField('name');
  validateField('email');
  validateField('password');
  validateField('confirmPassword');
  validateField('acceptTerms');

  return isFormValid.value;
};

const clearError = (): void => {
  error.value = '';
};

const clearErrors = (): void => {
  errors.name = '';
  errors.email = '';
  errors.password = '';
  errors.confirmPassword = '';
  errors.acceptTerms = '';
  error.value = '';
  successMessage.value = '';
};

const getErrorMessage = (authError: AuthError): string => {
  switch (authError.code) {
    case 'EMAIL_ALREADY_EXISTS':
      return 'Un compte existe déjà avec cette adresse email. Essayez de vous connecter ou utilisez une autre adresse.';

    case 'WEAK_PASSWORD':
      return 'Le mot de passe ne respecte pas les critères de sécurité requis.';

    case 'VALIDATION_ERROR':
      return 'Les données saisies ne sont pas valides. Vérifiez les informations et réessayez.';

    case 'NETWORK_ERROR':
      return 'Problème de connexion réseau. Vérifiez votre connexion internet.';

    case 'SERVER_ERROR':
      // Vérifier si c'est un message spécifique du serveur
      if (authError.message.includes('Email already registered')) {
        return 'Un compte existe déjà avec cette adresse email. Essayez de vous connecter.';
      }
      return 'Erreur serveur temporaire. Veuillez réessayer dans quelques instants.';

    case 'TOO_MANY_REQUESTS':
      return 'Trop de tentatives d\'inscription. Veuillez patienter avant de réessayer.';

    default:
      return authError.message || 'Une erreur inattendue s\'est produite lors de l\'inscription.';
  }
};

const handlePasswordStrengthChange = (strength: { score: number; label: string; percentage: number }): void => {
  passwordStrength.value = {
    score: strength.score,
    feedback: [],
    isValid: strength.score >= 3, // Considérer comme valide si score >= 3
  };
};

const handleSubmit = async (): Promise<void> => {
  // Nettoyer les erreurs précédentes
  clearErrors();

  // Valider le formulaire
  if (!validateForm()) {
    notification.value?.showNotification(
      'Veuillez corriger les erreurs dans le formulaire.',
      'error'
    );
    return;
  }

  isSubmitting.value = true;

  const userData: RegisterRequest = {
    name: formData.name.trim(),
    email: formData.email.trim().toLowerCase(),
    password: formData.password,
    fonction: formData.fonction.trim() || undefined,
    affiliation: formData.affiliation.trim() || undefined,
  };

  // Émettre l'événement de soumission - les erreurs seront gérées par le parent
  emit('submit', userData);

  // Note: isSubmitting sera remis à false par showError() ou par le parent via une méthode exposée
};

// Méthode pour afficher les erreurs depuis le parent
const showError = (err: unknown): void => {
  const authError = err as AuthError;

  // Afficher l'erreur via notification
  const errorMessage = getErrorMessage(authError);
  notification.value?.showNotification(errorMessage, 'error');

  // Mettre à jour l'état d'erreur pour l'affichage dans le template
  error.value = errorMessage;

  // Gestion spécifique selon le type d'erreur
  if (authError.code === 'EMAIL_ALREADY_EXISTS') {
    errors.email = 'Cette adresse email est déjà utilisée';
    // Ne pas vider l'email pour que l'utilisateur puisse le modifier
  } else if (authError.code === 'WEAK_PASSWORD') {
    errors.password = 'Mot de passe trop faible';
    formData.password = '';
    formData.confirmPassword = '';
  }

  // Arrêter le loading
  isSubmitting.value = false;

  console.error('Erreur d\'inscription:', authError);
};

// Méthode pour arrêter le loading (appelée par le parent en cas de succès)
const stopSubmitting = (): void => {
  isSubmitting.value = false;
};

// Classes computées
const submitButtonClasses = computed((): string => {
  const baseClasses = 'w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500';

  if (isSubmitting.value || !isFormValid.value) {
    return `${baseClasses} bg-gray-400 cursor-not-allowed`;
  }

  return `${baseClasses} bg-blue-600 hover:bg-blue-700`;
});

// Nettoyage au démontage du composant
const resetForm = (): void => {
  formData.name = '';
  formData.email = '';
  formData.password = '';
  formData.confirmPassword = '';
  formData.fonction = '';
  formData.affiliation = '';
  formData.acceptTerms = false;
  clearErrors();
  passwordStrength.value = { score: 0, feedback: [], isValid: false };
};

// Exposer les méthodes pour le composant parent
defineExpose({
  resetForm,
  clearErrors,
  validateForm,
  formData,
  setSuccessMessage: (message: string) => {
    successMessage.value = message;
  },
  showError,
  stopSubmitting,
});
</script>